/**
 * RequestorFormModal — create/edit one requestor (OPEN-053, ClickUp 86bc5hjn1).
 *
 * Mirrors DisposalFormModal's shape, which is the house idiom for a small catalog record.
 *
 * Two things here are load-bearing rather than cosmetic:
 *
 *  - **Edit sends a DIFF, not the whole record.** The PATCH body is `.strict()` with a
 *    `refine` that rejects `{}`, and every field is a tri-state (absent = leave alone,
 *    `null` = clear). Re-sending unchanged fields would work but would write an audit entry
 *    claiming fields changed when they did not — `REQUESTOR_UPDATED` records `Object.keys(b)`.
 *  - **There is no delete, only Deactivate.** `jobs.requestor_id` and
 *    `requestor_assignments.requestor_id` are both ON DELETE RESTRICT; deactivating is what
 *    "they left the company" means — old tickets stay readable, the person leaves the picker.
 *
 * DEC-147: `company` is a hauler-entered identifier and is uppercased (server-side too; the
 * input transform is just so the dispatcher isn't surprised by the saved value). `name`,
 * `phone` and `email` are contact info and are preserved exactly as typed.
 */
import * as React from 'react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from 'src/components/ui/dialog';
import { Button } from 'src/components/ui/button';
import { fieldStyle, labelStyle, FormRow } from '../sites-operators/form-bits';
import {
  useCreateRequestor,
  useUpdateRequestor,
  type Requestor,
  type RequestorBody,
} from './hooks';
import { formatNumber } from '@lodex-pro/shared/number-format';

export interface RequestorFormModalProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  /** null = create. */
  readonly editing: Requestor | null;
}

/** Empty input means "no value": null on edit (clears the column), omitted on create. */
function normalize(raw: string): string | null {
  const t = raw.trim();
  return t === '' ? null : t;
}

export function RequestorFormModal({
  open,
  onOpenChange,
  editing,
}: RequestorFormModalProps): React.JSX.Element {
  const [name, setName] = React.useState('');
  const [company, setCompany] = React.useState('');
  const [phone, setPhone] = React.useState('');
  const [email, setEmail] = React.useState('');
  const [loadEmails, setLoadEmails] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!open) return;
    setLoadEmails(editing?.loadEmailsEnabled ?? true);
    setName(editing?.name ?? '');
    setCompany(editing?.company ?? '');
    setPhone(editing?.phone ?? '');
    setEmail(editing?.email ?? '');
    setError(null);
  }, [open, editing]);

  const close = (): void => onOpenChange(false);

  const create = useCreateRequestor(() => {
    toast.success('Requestor added');
    close();
  });
  const update = useUpdateRequestor(editing?.id ?? '');
  const pending = create.isPending || update.isPending;

  const handleSave = (): void => {
    const trimmedName = name.trim();
    if (trimmedName === '') {
      setError('Name is required.');
      return;
    }
    setError(null);

    if (editing === null) {
      const body: RequestorBody = { name: trimmedName };
      const c = normalize(company);
      const p = normalize(phone);
      const e = normalize(email);
      if (c !== null) body.company = c;
      if (p !== null) body.phone = p;
      if (e !== null) body.email = e;
      create.mutate(body, {
        onError: () => setError('Could not add requestor. Check the email address and try again.'),
      });
      return;
    }

    // Diff against the loaded record — see the header note on why this is not a full replace.
    const body: RequestorBody = {};
    if (trimmedName !== editing.name) body.name = trimmedName;
    const c = normalize(company);
    const p = normalize(phone);
    const e = normalize(email);
    if (c !== editing.company) body.company = c;
    if (p !== editing.phone) body.phone = p;
    if (e !== editing.email) body.email = e;
    if (loadEmails !== (editing.loadEmailsEnabled ?? true)) body.loadEmailsEnabled = loadEmails;

    if (Object.keys(body).length === 0) {
      close();
      return;
    }
    update.mutate(body, {
      onSuccess: () => {
        toast.success('Requestor updated');
        close();
      },
      onError: () => setError('Could not save. Check the email address and try again.'),
    });
  };

  const toggleActive = (): void => {
    if (editing === null) return;
    const next = !editing.isActive;
    update.mutate(
      { isActive: next },
      {
        onSuccess: () => {
          toast.success(next ? 'Requestor reactivated' : 'Requestor deactivated');
          close();
        },
        onError: () => setError('Could not change status. Try again.'),
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{editing === null ? 'Add requestor' : 'Edit requestor'}</DialogTitle>
        </DialogHeader>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <FormRow label="Name" htmlFor="rq-name" required>
            <input
              id="rq-name"
              value={name}
              onChange={(e): void => setName(e.target.value)}
              placeholder="Who asks for the load"
              style={fieldStyle}
            />
          </FormRow>
          <FormRow label="Company" htmlFor="rq-company">
            <input
              id="rq-company"
              value={company}
              onChange={(e): void => setCompany(e.target.value.toUpperCase())}
              placeholder="Optional"
              style={{ ...fieldStyle, textTransform: 'uppercase' }}
            />
          </FormRow>
          <FormRow label="Phone" htmlFor="rq-phone">
            <input
              id="rq-phone"
              value={phone}
              onChange={(e): void => setPhone(e.target.value)}
              placeholder="Optional"
              style={fieldStyle}
            />
          </FormRow>
          <FormRow label="Email" htmlFor="rq-email">
            <input
              id="rq-email"
              type="email"
              value={email}
              onChange={(e): void => setEmail(e.target.value)}
              placeholder="Optional"
              style={fieldStyle}
            />
          </FormRow>
          {/* Phase 17.8 (D-13) — whether this person gets the load-complete email for loads
              they are ticked as the requestor on. Only applies when the hauler-wide switch in
              Settings is on. New requestors start ON. */}
          {editing !== null && (
            <label
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                minHeight: '56px',
                fontSize: '16px',
              }}
            >
              <input
                type="checkbox"
                style={{ width: '24px', height: '24px' }}
                checked={loadEmails}
                onChange={(ev): void => setLoadEmails(ev.target.checked)}
              />
              Send Haul Confirmation emails to this requestor
            </label>
          )}
          {editing?.unsubscribed === true && (
            <p
              role="note"
              style={{ ...labelStyle, textTransform: 'none', color: 'var(--color-danger)' }}
            >
              This requestor unsubscribed from Haul Confirmation emails. Only they can turn them
              back on, from the link in an earlier email.
            </p>
          )}
          {editing?.emailStatus === 'BOUNCING' && (
            <p
              role="note"
              style={{ ...labelStyle, textTransform: 'none', color: 'var(--color-danger)' }}
            >
              Emails to this address are bouncing. Correct the email address to start sending again.
            </p>
          )}
          {/* A count, not a blocker: deactivating leaves the assignments in place so the
              person can be reactivated without re-assigning every site. */}
          {editing !== null && editing.assignmentCount > 0 && (
            <p
              style={{ ...labelStyle, textTransform: 'none', color: 'var(--color-text-tertiary)' }}
            >
              Assigned to {formatNumber(editing.assignmentCount)}{' '}
              {editing.assignmentCount === 1 ? 'site or well' : 'sites and wells'}.
            </p>
          )}
          {error !== null && (
            <p
              role="alert"
              style={{ ...labelStyle, textTransform: 'none', color: 'var(--color-danger)' }}
            >
              {error}
            </p>
          )}
        </div>
        <DialogFooter>
          {editing !== null && (
            <Button
              type="button"
              variant="outline"
              disabled={pending}
              onClick={toggleActive}
              style={
                editing.isActive
                  ? { color: 'var(--color-danger)', borderColor: 'var(--color-danger)' }
                  : undefined
              }
            >
              {editing.isActive ? 'Deactivate' : 'Reactivate'}
            </Button>
          )}
          <Button type="button" variant="outline" onClick={close}>
            Cancel
          </Button>
          <Button
            type="button"
            disabled={pending}
            onClick={handleSave}
            style={{
              backgroundColor: 'var(--color-accent-blue)',
              color: 'var(--color-on-accent)',
              borderColor: 'var(--color-accent-blue)',
            }}
          >
            {pending ? 'Saving…' : editing === null ? 'Add' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
